// ---------------------------------------------------------------------------
// case-studies.i18n-asia.ts — Asian + Arabic locale translations for CaseStudiesPage
// ---------------------------------------------------------------------------
import type { CaseStudiesContent } from './case-studies.types';

type MetricBlock = { before: string[]; after: string[]; change: string[] };

const APEX_METRICS: MetricBlock = {
  before: ['8 min', '12.4%', '18', 'INR 4,200', '52 min', '34', '45 min', '86 hrs'],
  after: ['90 sec', '0.8%', '0', 'INR 5,880', '28 min', '72', '4 min', '22 hrs'],
  change: ['-81%', '-93%', '-100%', '+40%', '-46%', '+112%', '-91%', '-74%'],
};

const PALAZZO_METRICS: MetricBlock = {
  before: ['$142', '$198', '61%', '22 hrs', '18%', '$8.2K'],
  after: ['$196', '$242', '94%', '3 hrs', '34%', '$24.6K'],
  change: ['+38%', '+22%', '+54%', '-86%', '+89%', '+200%'],
};

const CITYSTAY_METRICS: MetricBlock = {
  before: ['12 min', '340', '38', '32%', '14 rooms/shift', '$0'],
  after: ['18 sec', '128', '67', '14%', '19 rooms/shift', '$186K'],
  change: ['-97%', '-62%', '+76%', '-56%', '+36%', 'NEW'],
};

function buildStudy(d: {
  slug: string; hotelName: string; logo: string;
  location: string; propertyType: string; roomCount: string;
  challenge: string; solution: string;
  metricLabels: string[]; metricData: MetricBlock;
  testimonialQuote: string; testimonialAuthor: string; testimonialRole: string;
  tags: string[]; implTime: string;
}) {
  return {
    slug: d.slug, hotelName: d.hotelName, logo: d.logo,
    location: d.location, propertyType: d.propertyType, roomCount: d.roomCount,
    challenge: d.challenge, solution: d.solution,
    metrics: d.metricLabels.map((label, i) => ({
      label, before: d.metricData.before[i], after: d.metricData.after[i], change: d.metricData.change[i],
    })),
    testimonial: { quote: d.testimonialQuote, author: d.testimonialAuthor, role: d.testimonialRole },
    tags: d.tags, implementationTime: d.implTime,
  };
}

/* -- Japanese (JA) ------------------------------------------------------- */
export const ja: CaseStudiesContent = {
  header: { title: '導入事例', description: '実際の施設による実際の成果。ホテルやリゾートがHotelStack AIを使って業務をどう変革したかをご覧ください。' },
  overviewTitle: 'すべての施設で実証された効果',
  overviewDescription: '25のAI機能を備えたHotelStackは収益、効率性、ゲスト満足度において一貫して測定可能な改善を実現します。',
  overviewStats: [
    { label: '平均収益増加(AI)', value: '38%' },
    { label: 'AI駆動コスト削減', value: '34%' },
    { label: 'ゲスト満足度向上', value: '47%' },
    { label: 'スタッフ生産性向上', value: '42%' },
  ],
  challengeLabel: '課題',
  solutionLabel: 'ソリューション',
  metricsLabel: '主要成果',
  beforeLabel: '導入前',
  afterLabel: '導入後',
  implementationLabel: '導入期間',
  tagsLabel: '使用モジュール',
  ctaTitle: '施設の変革を始めませんか？',
  ctaDescription: '既にHotelStack AIで収益向上とコスト削減を実現している2,500以上のホテルに参加しましょう。',
  ctaButton: 'デモを予約',
  studies: [buildStudy({
    slug: 'apex-residency', hotelName: 'ApexResidency', logo: 'AR',
    location: 'ムンバイ、インド', propertyType: 'ラグジュアリーブティックホテル', roomCount: '120室',
    challenge: 'ApexResidencyは旧式のオンプレミスPMSで運営。予約の手動入力、電話によるハウスキーピング調整、スプレッドシートでの収益レポート。チェックイン平均8分、収益漏れが月12%超。',
    solution: 'HotelStackが統合クラウドプラットフォームに全面移行。90秒デジタルチェックイン。50以上のOTAでリアルタイム在庫同期。AIダイナミックプライシングがML需要シグナルで料金最適化。AIナイトオーディット異常検出が初月INR 2.3Lの請求エラーを発見。AIコンシェルジュチャットボットがゲスト問合せの65%を自律処理。',
    metricLabels: ['平均チェックイン時間', '月間収益漏れ', 'OTA重複予約/月', 'RevPAR', 'ハウスキーピング所要時間', 'ゲスト満足度(NPS)', 'ナイトオーディット時間', '残業時間/週'],
    metricData: APEX_METRICS, testimonialAuthor: 'Rajesh Menon',
    testimonialQuote: 'HotelStackは一夜にして業務を変革。収益漏れを解消し、チェックイン80%短縮、AIプライシングでRevPARがさらに18%向上しました。',
    testimonialRole: '総支配人、ApexResidency',
    tags: ['フロントデスク', 'チャネルマネージャー', 'ハウスキーピング', 'AIプライシング', 'AIナイトオーディット', 'AIコンシェルジュ', 'Folio', 'POS'],
    implTime: '3週間',
  }), buildStudy({
    slug: 'grand-palazzo-bali', hotelName: 'The Grand Palazzo', logo: 'GP',
    location: 'バリ、インドネシア', propertyType: 'ラグジュアリービーチリゾート', roomCount: '280室',
    challenge: '平均稼働率72%にもかかわらず収益停滞。手動料金設定は市場の需要変化に対応できず。需要予測なし。レベニューチームは週22時間以上を料金分析スプレッドシートに費やしていた。',
    solution: 'HotelStack AIスイート導入：AIダイナミックプライシングが50以上の需要シグナルを分析し4時間ごとに自動料金調整。AI需要予測94%精度。AIコンペティター料金モニター。スマートアップセルが付帯収益200%増。',
    metricLabels: ['RevPAR', 'ADR(平均日次料金)', '予測精度', '手動料金作業/週', 'ダイレクト予約率', 'アップセル収益/月'],
    metricData: PALAZZO_METRICS, testimonialAuthor: 'Wayan Sudiarta',
    testimonialQuote: 'AIプライシングエンジンは11日で投資回収。初四半期でRevPARが38%上昇し、需要予測で季節的なスタッフ配置の推測が完全に不要になりました。',
    testimonialRole: 'レベニューディレクター、The Grand Palazzo',
    tags: ['AIプライシング', '需要予測', 'コンペティターモニター', 'スマートアップセル', 'レベニューマネジメント', 'チャネルマネージャー'],
    implTime: '2週間',
  }), buildStudy({
    slug: 'citystay-singapore', hotelName: 'CityStay Hotels', logo: 'CS',
    location: 'シンガポール(4施設)', propertyType: 'ビジネスホテルチェーン', roomCount: '520室(合計)',
    challenge: '4施設で平均応答時間12分。340件以上の日次問合せを手動処理、年間離職率32%。印刷リストによるハウスキーピング割当、リアルタイム調整なし。',
    solution: 'AIコンシェルジュチャットボットが4言語で78%の問合せを自律処理。AIスマートハウスキーピング割当。AIスタッフスケジューリング最適化。AIゲストセンチメント分析でプロアクティブなサービス回復。',
    metricLabels: ['ゲスト応答時間', 'フロント電話/日', 'ゲスト満足度(NPS)', 'スタッフ離職率', 'ハウスキーピング効率', '年間人件費削減'],
    metricData: CITYSTAY_METRICS, testimonialAuthor: 'Lim Wei Ting',
    testimonialQuote: 'AIコンシェルジュで全てが変わりました。深夜3時でもゲストの言語で即時回答、NPSは3ヶ月で29ポイント上昇。4施設全体の一貫性は驚異的です。',
    testimonialRole: 'VP オペレーションズ、CityStay Hotels',
    tags: ['AIコンシェルジュ', 'スマートハウスキーピング', 'AIスケジューリング', 'センチメント分析', 'コミュニケーション', 'ゲストポータル'],
    implTime: '4週間',
  })],
};

/* -- Chinese Simplified (ZH) --------------------------------------------- */
export const zh: CaseStudiesContent = {
  header: { title: '客户案例', description: '真实酒店的真实成果。了解酒店和度假村如何使用HotelStack AI转型运营。' },
  overviewTitle: '所有酒店的验证效果',
  overviewDescription: '拥有25项AI功能的HotelStack持续在收入、效率和客人满意度方面带来可衡量的改善。',
  overviewStats: [
    { label: '平均收入增长(AI)', value: '38%' },
    { label: 'AI驱动成本节省', value: '34%' },
    { label: '客人满意度提升', value: '47%' },
    { label: '员工生产力提升', value: '42%' },
  ],
  challengeLabel: '挑战',
  solutionLabel: '解决方案',
  metricsLabel: '关键成果',
  beforeLabel: '之前',
  afterLabel: '之后',
  implementationLabel: '实施周期',
  tagsLabel: '使用模块',
  ctaTitle: '准备好转型您的酒店了吗？',
  ctaDescription: '加入已使用HotelStack AI提升收入和降低成本的2,500多家酒店。',
  ctaButton: '预约演示',
  studies: [buildStudy({
    slug: 'apex-residency', hotelName: 'ApexResidency', logo: 'AR',
    location: '孟买，印度', propertyType: '豪华精品酒店', roomCount: '120间客房',
    challenge: 'ApexResidency使用过时的本地PMS运营，需要手动输入预订数据、通过电话协调客房清洁。入住登记平均8分钟，收入流失每月超过12%。',
    solution: 'HotelStack用统一云平台替代整个运营栈。90秒数字入住登记。50多个OTA实时同步。AI动态定价用ML需求信号优化房价。AI夜审异常检测第一个月发现INR 2.3L账单错误。AI礼宾聊天机器人自主处理65%的客人查询。',
    metricLabels: ['平均入住时间', '月收入流失', 'OTA超售/月', 'RevPAR', '客房清洁周转', '客人满意度(NPS)', '夜审时长', '加班时间/周'],
    metricData: APEX_METRICS, testimonialAuthor: 'Rajesh Menon',
    testimonialQuote: 'HotelStack一夜之间改变了运营。消除了收入流失，入住时间缩短80%，AI定价使RevPAR额外提高18%。',
    testimonialRole: '总经理，ApexResidency',
    tags: ['前台', '渠道管理器', '客房部', 'AI定价', 'AI夜审', 'AI礼宾', '账单', 'POS'],
    implTime: '3周',
  }), buildStudy({
    slug: 'grand-palazzo-bali', hotelName: 'The Grand Palazzo', logo: 'GP',
    location: '巴厘岛，印度尼西亚', propertyType: '豪华海滨度假村', roomCount: '280间客房',
    challenge: '尽管平均入住率72%，收入停滞。手动定价错过市场需求变化。无需求预测。收益团队每周花22小时在价格分析表格上。',
    solution: 'HotelStack AI套件：AI动态定价分析50+需求信号每4小时自动调整房价。AI需求预测94%准确率。AI竞争对手价格监控。智能追加销售推荐使附加收入增长200%。',
    metricLabels: ['RevPAR', 'ADR(平均日房价)', '预测准确率', '手动定价时间/周', '直接预订占比', '追加销售收入/月'],
    metricData: PALAZZO_METRICS, testimonialAuthor: 'Wayan Sudiarta',
    testimonialQuote: 'AI定价引擎11天收回成本。首季RevPAR跃升38%，需求预测完全消除了季节性人员配置猜测。',
    testimonialRole: '收益总监，The Grand Palazzo',
    tags: ['AI定价', '需求预测', '竞争对手监控', '智能追加销售', '收益管理', '渠道管理器'],
    implTime: '2周',
  }), buildStudy({
    slug: 'citystay-singapore', hotelName: 'CityStay Hotels', logo: 'CS',
    location: '新加坡(4家酒店)', propertyType: '商务连锁酒店', roomCount: '520间客房(合计)',
    challenge: '4家酒店平均响应时间12分钟。每日340+查询手动处理，年员工流失率32%。纸质清单分配客房清洁，无实时调整。',
    solution: 'AI礼宾聊天机器人4种语言自主处理78%查询。AI智能客房清洁分配。AI排班优化器。AI客人情感分析实现主动服务恢复。',
    metricLabels: ['客人响应时间', '前台电话/日', '客人满意度(NPS)', '员工流失率', '客房清洁效率', '年度人力成本节省'],
    metricData: CITYSTAY_METRICS, testimonialAuthor: 'Lim Wei Ting',
    testimonialQuote: 'AI礼宾改变了一切。客人凌晨3点用自己的语言获得即时回答，NPS在3个月内上升29分。4家酒店的一致性令人惊叹。',
    testimonialRole: '运营副总裁，CityStay Hotels',
    tags: ['AI礼宾', '智能客房清洁', 'AI排班', '情感分析', '通信', '客人门户'],
    implTime: '4周',
  })],
};

/* -- Arabic (AR) --------------------------------------------------------- */
const arApexMetrics: MetricBlock = {
  before: ['٨ دقائق', '١٢٫٤%', '١٨', '٤٬٢٠٠ روبية', '٥٢ دقيقة', '٣٤', '٤٥ دقيقة', '٨٦ ساعة'],
  after: ['٩٠ ثانية', '٠٫٨%', '٠', '٥٬٨٨٠ روبية', '٢٨ دقيقة', '٧٢', '٤ دقائق', '٢٢ ساعة'],
  change: ['-٨١%', '-٩٣%', '-١٠٠%', '+٤٠%', '-٤٦%', '+١١٢%', '-٩١%', '-٧٤%'],
};

export const ar: CaseStudiesContent = {
  header: { title: 'دراسات الحالة', description: 'نتائج حقيقية من فنادق حقيقية. تعرف على كيفية استخدام الفنادق للذكاء الاصطناعي في HotelStack لتحويل عملياتها.' },
  overviewTitle: 'تأثير مُثبت عبر جميع المنشآت',
  overviewDescription: 'يقدم HotelStack بـ ٢٥ ميزة ذكاء اصطناعي تحسينات قابلة للقياس في الإيرادات والكفاءة ورضا النزلاء.',
  overviewStats: [
    { label: 'متوسط زيادة الإيرادات (AI)', value: '٣٨%' },
    { label: 'وفورات بالذكاء الاصطناعي', value: '٣٤%' },
    { label: 'تحسين رضا النزلاء', value: '٤٧%' },
    { label: 'زيادة إنتاجية الموظفين', value: '٤٢%' },
  ],
  challengeLabel: 'التحدي',
  solutionLabel: 'الحل',
  metricsLabel: 'النتائج الرئيسية',
  beforeLabel: 'قبل',
  afterLabel: 'بعد',
  implementationLabel: 'مدة التنفيذ',
  tagsLabel: 'الوحدات المستخدمة',
  ctaTitle: 'هل أنت مستعد لتحويل منشأتك؟',
  ctaDescription: 'انضم إلى أكثر من ٢٬٥٠٠ فندق يستخدمون بالفعل الذكاء الاصطناعي في HotelStack لزيادة الإيرادات وخفض التكاليف.',
  ctaButton: 'جدولة عرض توضيحي',
  studies: [{
    slug: 'apex-residency', hotelName: 'ApexResidency', logo: 'AR',
    location: 'مومباي، الهند', propertyType: 'فندق بوتيك فاخر', roomCount: '١٢٠ غرفة',
    challenge: 'كان ApexResidency يعمل بنظام PMS محلي قديم. إدخال بيانات يدوي، تنسيق التنظيف بالهاتف، تقارير بالجداول. متوسط تسجيل الدخول ٨ دقائق، تسرب الإيرادات تجاوز ١٢% شهرياً.',
    solution: 'استبدل HotelStack كامل المنظومة بمنصة سحابية. تسجيل دخول رقمي ٩٠ ثانية. مزامنة ٥٠+ OTA. التسعير الديناميكي بالذكاء الاصطناعي يحسّن الأسعار بإشارات الطلب. كشف شذوذ التدقيق الليلي بالذكاء الاصطناعي اكتشف ٢٫٣ لاكه روبية أخطاء فوترة في الشهر الأول. روبوت الكونسيرج يعالج ٦٥% من استفسارات النزلاء تلقائياً.',
    metrics: ['متوسط وقت تسجيل الدخول', 'تسرب الإيرادات الشهري', 'حجوزات زائدة/شهر', 'العائد لكل غرفة متاحة', 'وقت تجهيز الغرف', 'رضا النزلاء', 'مدة التدقيق الليلي', 'ساعات العمل الإضافي/أسبوع'].map((label, i) => ({
      label, before: arApexMetrics.before[i], after: arApexMetrics.after[i], change: arApexMetrics.change[i],
    })),
    testimonial: { quote: 'حوّل HotelStack عملياتنا بين عشية وضحاها. قضينا على تسرب الإيرادات وقلصنا تسجيل الدخول ٨٠% والتسعير بالذكاء الاصطناعي رفع العائد ١٨% إضافية.', author: 'راجيش مينون', role: 'المدير العام، ApexResidency' },
    tags: ['الاستقبال', 'مدير القنوات', 'التنظيف', 'تسعير ذكي', 'تدقيق ليلي ذكي', 'كونسيرج ذكي', 'الفوليو', 'نقطة البيع'],
    implementationTime: '٣ أسابيع',
  }, buildStudy({
    slug: 'grand-palazzo-bali', hotelName: 'The Grand Palazzo', logo: 'GP',
    location: 'بالي، إندونيسيا', propertyType: 'منتجع شاطئي فاخر', roomCount: '٢٨٠ غرفة',
    challenge: 'ركود الإيرادات رغم إشغال ٧٢%. التسعير اليدوي فوّت تغيرات الطلب. لا توقعات للطلب. فريق الإيرادات قضى ٢٢+ ساعة أسبوعياً على جداول تحليل الأسعار.',
    solution: 'مجموعة HotelStack الذكية: التسعير الديناميكي يحلل ٥٠+ إشارة طلب ويعدل الأسعار كل ٤ ساعات تلقائياً. توقع الطلب بدقة ٩٤%. مراقب أسعار المنافسين. توصيات البيع المتقاطع الذكي زادت الإيرادات الإضافية ٢٠٠%.',
    metricLabels: ['العائد لكل غرفة', 'متوسط السعر اليومي', 'دقة التوقع', 'ساعات التسعير اليدوي/أسبوع', 'الحجوزات المباشرة', 'إيرادات البيع المتقاطع/شهر'],
    metricData: PALAZZO_METRICS, testimonialAuthor: 'وايان سوديارتا',
    testimonialQuote: 'محرك التسعير الذكي استرد تكلفته في ١١ يوماً. ارتفع العائد لكل غرفة ٣٨% في الربع الأول وتوقع الطلب أزال تخمينات التوظيف الموسمي تماماً.',
    testimonialRole: 'مدير الإيرادات، The Grand Palazzo',
    tags: ['تسعير ذكي', 'توقع الطلب', 'مراقب المنافسين', 'البيع المتقاطع الذكي', 'إدارة الإيرادات', 'مدير القنوات'],
    implTime: 'أسبوعان',
  }), buildStudy({
    slug: 'citystay-singapore', hotelName: 'CityStay Hotels', logo: 'CS',
    location: 'سنغافورة (٤ فنادق)', propertyType: 'سلسلة فنادق أعمال', roomCount: '٥٢٠ غرفة (إجمالي)',
    challenge: 'متوسط وقت الاستجابة ١٢ دقيقة عبر ٤ فنادق. ٣٤٠+ استفسار يومي يُعالج يدوياً، معدل دوران سنوي ٣٢%. توزيع التنظيف بقوائم مطبوعة بدون تعديلات فورية.',
    solution: 'روبوت الكونسيرج الذكي يعالج ٧٨% من الاستفسارات بـ ٤ لغات تلقائياً. توزيع التنظيف الذكي. مُحسّن جداول الموظفين بالذكاء الاصطناعي. تحليل مشاعر النزلاء لتدخل استباقي.',
    metricLabels: ['وقت استجابة النزلاء', 'اتصالات الاستقبال/يوم', 'رضا النزلاء (NPS)', 'معدل دوران الموظفين', 'كفاءة التنظيف', 'وفورات العمالة السنوية'],
    metricData: CITYSTAY_METRICS, testimonialAuthor: 'ليم وي تينج',
    testimonialQuote: 'الكونسيرج الذكي غيّر كل شيء. النزلاء يحصلون على إجابات فورية الساعة ٣ صباحاً بلغتهم، وارتفع NPS ٢٩ نقطة في ٣ أشهر. الاتساق عبر ٤ فنادق مذهل.',
    testimonialRole: 'نائب رئيس العمليات، CityStay Hotels',
    tags: ['كونسيرج ذكي', 'تنظيف ذكي', 'جدولة ذكية', 'تحليل المشاعر', 'الاتصالات', 'بوابة النزلاء'],
    implTime: '٤ أسابيع',
  })],
};

/* -- Thai (TH) ----------------------------------------------------------- */
export const th: CaseStudiesContent = {
  header: { title: 'กรณีศึกษา', description: 'ผลลัพธ์จริงจากโรงแรมจริง ดูว่าโรงแรมและรีสอร์ทใช้ HotelStack AI เปลี่ยนแปลงการดำเนินงานอย่างไร' },
  overviewTitle: 'ผลกระทบที่พิสูจน์แล้วในทุกโรงแรม',
  overviewDescription: 'HotelStack พร้อม 25 ฟีเจอร์ AI ส่งมอบการปรับปรุงที่วัดผลได้ในด้านรายได้ ประสิทธิภาพ และความพึงพอใจของแขก',
  overviewStats: [
    { label: 'รายได้เพิ่มขึ้นเฉลี่ย (AI)', value: '38%' },
    { label: 'ประหยัดต้นทุนด้วย AI', value: '34%' },
    { label: 'ความพึงพอใจแขกดีขึ้น', value: '47%' },
    { label: 'ผลิตภาพพนักงานเพิ่มขึ้น', value: '42%' },
  ],
  challengeLabel: 'ความท้าทาย',
  solutionLabel: 'โซลูชัน',
  metricsLabel: 'ผลลัพธ์สำคัญ',
  beforeLabel: 'ก่อน',
  afterLabel: 'หลัง',
  implementationLabel: 'ระยะเวลาติดตั้ง',
  tagsLabel: 'โมดูลที่ใช้',
  ctaTitle: 'พร้อมที่จะเปลี่ยนแปลงโรงแรมของคุณแล้วหรือยัง?',
  ctaDescription: 'เข้าร่วมกับโรงแรมกว่า 2,500 แห่งที่ใช้ HotelStack AI เพิ่มรายได้และลดต้นทุน',
  ctaButton: 'นัดสาธิต',
  studies: [buildStudy({
    slug: 'apex-residency', hotelName: 'ApexResidency', logo: 'AR',
    location: 'มุมไบ, อินเดีย', propertyType: 'โรงแรมบูติกหรู', roomCount: '120 ห้อง',
    challenge: 'ApexResidency ใช้ระบบ PMS แบบ on-premise รุ่นเก่า กรอกข้อมูลด้วยมือ ประสานงานแม่บ้านผ่านโทรศัพท์ เช็คอินเฉลี่ย 8 นาที การรั่วไหลรายได้เกิน 12% ต่อเดือน',
    solution: 'HotelStack เปลี่ยนระบบทั้งหมดด้วยแพลตฟอร์มคลาวด์ เช็คอินดิจิทัล 90 วินาที Channel Manager ซิงค์ 50+ OTA AI Dynamic Pricing ปรับราคาด้วยสัญญาณ ML AI Night Audit ตรวจพบข้อผิดพลาดบิล INR 2.3L ในเดือนแรก AI Concierge Chatbot จัดการ 65% คำถามแขกอัตโนมัติ',
    metricLabels: ['เวลาเช็คอินเฉลี่ย', 'การรั่วไหลรายได้/เดือน', 'OTA จองเกิน/เดือน', 'RevPAR', 'เวลาทำความสะอาดห้อง', 'ความพึงพอใจแขก (NPS)', 'เวลา Night Audit', 'ชั่วโมง OT/สัปดาห์'],
    metricData: APEX_METRICS, testimonialAuthor: 'Rajesh Menon',
    testimonialQuote: 'HotelStack เปลี่ยนการดำเนินงานข้ามคืน ขจัดรายได้รั่วไหล ลดเช็คอิน 80% AI Pricing เพิ่ม RevPAR อีก 18%',
    testimonialRole: 'ผู้จัดการทั่วไป, ApexResidency',
    tags: ['แผนกต้อนรับ', 'Channel Manager', 'แม่บ้าน', 'AI Pricing', 'AI Night Audit', 'AI Concierge', 'Folio', 'POS'],
    implTime: '3 สัปดาห์',
  }), buildStudy({
    slug: 'grand-palazzo-bali', hotelName: 'The Grand Palazzo', logo: 'GP',
    location: 'บาหลี, อินโดนีเซีย', propertyType: 'รีสอร์ทชายหาดหรู', roomCount: '280 ห้อง',
    challenge: 'รายได้หยุดนิ่งแม้ occupancy เฉลี่ย 72% การตั้งราคาด้วยมือพลาดการเปลี่ยนแปลงอุปสงค์ ไม่มีการคาดการณ์อุปสงค์ ทีม Revenue ใช้เวลา 22+ ชั่วโมง/สัปดาห์วิเคราะห์ราคา',
    solution: 'HotelStack AI Suite: AI Dynamic Pricing วิเคราะห์ 50+ สัญญาณอุปสงค์ปรับราคาอัตโนมัติทุก 4 ชั่วโมง AI คาดการณ์อุปสงค์แม่นยำ 94% AI ติดตามราคาคู่แข่ง Smart Upsell เพิ่มรายได้เสริม 200%',
    metricLabels: ['RevPAR', 'ADR (ราคาเฉลี่ยต่อวัน)', 'ความแม่นยำคาดการณ์', 'ชั่วโมงตั้งราคาด้วยมือ/สัปดาห์', 'สัดส่วนจองตรง', 'รายได้ Upsell/เดือน'],
    metricData: PALAZZO_METRICS, testimonialAuthor: 'Wayan Sudiarta',
    testimonialQuote: 'AI Pricing Engine คุ้มทุนใน 11 วัน RevPAR เพิ่ม 38% ในไตรมาสแรก และ AI คาดการณ์ขจัดการเดาจัดพนักงานตามฤดูกาลทั้งหมด',
    testimonialRole: 'ผู้อำนวยการ Revenue, The Grand Palazzo',
    tags: ['AI Pricing', 'คาดการณ์อุปสงค์', 'ติดตามคู่แข่ง', 'Smart Upsell', 'Revenue Management', 'Channel Manager'],
    implTime: '2 สัปดาห์',
  }), buildStudy({
    slug: 'citystay-singapore', hotelName: 'CityStay Hotels', logo: 'CS',
    location: 'สิงคโปร์ (4 โรงแรม)', propertyType: 'เครือโรงแรมธุรกิจ', roomCount: '520 ห้อง (รวม)',
    challenge: 'เวลาตอบสนองเฉลี่ย 12 นาทีใน 4 โรงแรม 340+ คำถามต่อวันจัดการด้วยมือ อัตราลาออก 32%/ปี จัดแม่บ้านด้วยรายการกระดาษไม่มีการปรับเรียลไทม์',
    solution: 'AI Concierge Chatbot จัดการ 78% คำถามใน 4 ภาษาอัตโนมัติ AI Smart Housekeeping จัดสรรเส้นทางทำความสะอาด AI Staff Scheduling Optimizer AI Sentiment Analysis สำหรับการแก้ไขบริการเชิงรุก',
    metricLabels: ['เวลาตอบแขก', 'โทรแผนกต้อนรับ/วัน', 'ความพึงพอใจแขก (NPS)', 'อัตราลาออกพนักงาน', 'ประสิทธิภาพแม่บ้าน', 'ประหยัดค่าแรงรายปี'],
    metricData: CITYSTAY_METRICS, testimonialAuthor: 'Lim Wei Ting',
    testimonialQuote: 'AI Concierge เปลี่ยนทุกอย่าง แขกได้คำตอบทันทีตี 3 ในภาษาของตัวเอง NPS เพิ่ม 29 คะแนนใน 3 เดือน ความสม่ำเสมอใน 4 โรงแรมน่าทึ่ง',
    testimonialRole: 'VP Operations, CityStay Hotels',
    tags: ['AI Concierge', 'Smart Housekeeping', 'AI Scheduling', 'Sentiment Analysis', 'Communications', 'Guest Portal'],
    implTime: '4 สัปดาห์',
  })],
};
